import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ContentSection from '../common/ContentSection/ContentSection';
import { Skeleton } from '../PrimereactComponents';
import { useFormatters } from '../../utils/texts';
import { getApiClient } from '../../api';
import type { CouncilMember, ComparisonMetric, MemberComparison } from '../../api';
import { useMunicipalityId } from '../../hooks/useCouncilPath';

type Props = {
    memberData: CouncilMember;
    partyColor?: string;
};

// Below this many members, a percentile reads as more precise than the underlying
// sample actually supports.
const MIN_MEMBERS_FOR_PERCENTILE = 5;

type MetricRowProps = {
    label: string;
    metric: ComparisonMetric;
    partyColor?: string;
    percentileText?: string;
    formatter: ReturnType<typeof useFormatters>;
};

const ComparisonMetricRow = ({ label, metric, partyColor, percentileText, formatter }: MetricRowProps) => (
    <div className='agreement-item'>
        <div className='agreement-row'>
            <span className='agreement-label'>{label}</span>
            <span className='agreement-track'>
                <span style={{ width: `${metric.hodnota}%`, backgroundColor: partyColor }} />
                {metric.prumer_klubu != null && (
                    <i className='agreement-marker agreement-marker-party' style={{ left: `${metric.prumer_klubu}%` }} />
                )}
                <i className='agreement-marker agreement-marker-assembly' style={{ left: `${metric.prumer_snemu}%` }} />
            </span>
            <span className='agreement-pct'>{formatter.percentFrom100(metric.hodnota)}</span>
        </div>
        {percentileText && <p className='agreement-percentile'>{percentileText}</p>}
    </div>
);

const MemberComparisonSection = ({ memberData, partyColor }: Props) => {
    const { t, i18n } = useTranslation('members');
    const formatter = useFormatters(i18n.language);
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const [comparison, setComparison] = useState<MemberComparison>();

    useEffect(() => {
        if (!municipalitaId || !bodyType || !zastupitelstvoId) return;
        setComparison(undefined);

        let cancelled = false;
        const api = getApiClient();
        api.getComparisonByMunicipalityIdAndBodyAndCouncilIdAndMemberId(
            municipalitaId,
            bodyType,
            parseInt(zastupitelstvoId, 10),
            memberData.id
        )
            .then((data) => {
                if (!cancelled) setComparison(data);
            })
            .catch((error) => console.error('Error fetching member comparison data:', error));

        return () => {
            cancelled = true;
        };
    }, [memberData.id, municipalitaId, bodyType, zastupitelstvoId]);

    const participation = comparison?.ucast_v_hlasovani;
    const agreement = comparison?.shoda_s_klubem;

    const participationPercentileText =
        participation && participation.percentil != null && participation.pocet_clenu >= MIN_MEMBERS_FOR_PERCENTILE
            ? t('MemberDetail.higherParticipationThan', { percent: formatter.percentFrom100(participation.percentil) })
            : undefined;

    const agreementPercentileText =
        agreement && agreement.percentil != null && agreement.pocet_clenu >= MIN_MEMBERS_FOR_PERCENTILE
            ? t('MemberDetail.higherAgreementThan', { percent: formatter.percentFrom100(agreement.percentil) })
            : undefined;

    return (
        <ContentSection
            title={t('MemberDetail.comparisonSectionTitle')}
            subtitle={t('MemberDetail.comparisonSubtitle')}
            className='dashboard-card member-comparison-card'
        >
            <div className='agreement-legend'>
                <span>
                    <i className='agreement-legend-swatch agreement-legend-swatch-member' style={{ backgroundColor: partyColor }} />
                    {memberData.jmeno}
                </span>
                <span>
                    <i className='agreement-marker agreement-marker-party agreement-legend-marker' />
                    {t('MemberDetail.partyAverage')}
                </span>
                <span>
                    <i className='agreement-marker agreement-marker-assembly agreement-legend-marker' />
                    {t('MemberDetail.assemblyAverage')}
                </span>
            </div>
            {participation && agreement ? (
                <div className='agreement-list'>
                    <ComparisonMetricRow
                        label={t('MemberDetail.votingParticipationMetric')}
                        metric={participation}
                        partyColor={partyColor}
                        percentileText={participationPercentileText}
                        formatter={formatter}
                    />
                    <ComparisonMetricRow
                        label={t('MemberDetail.ownPartyAgreementMetric')}
                        metric={agreement}
                        partyColor={partyColor}
                        percentileText={agreementPercentileText}
                        formatter={formatter}
                    />
                </div>
            ) : (
                <div className='agreement-list'>
                    <Skeleton height='3.5rem' style={{ marginBottom: '0.5rem' }} />
                    <Skeleton height='3.5rem' />
                </div>
            )}
        </ContentSection>
    );
};

export default MemberComparisonSection;
